import { signup_path } from '@/rails/routes';
import { Link } from '@inertiajs/react';
import type { ReactNode } from 'react';

const CONTACT_EMAIL = 'lance.foley@caprocklabs.com';

const SECTION = 'border-t py-14 sm:py-20';
const CONTAINER = 'mx-auto max-w-6xl px-4 sm:px-6';
const FOCUS_RING =
  'rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring';
const TEXT_LINK = `underline decoration-accent underline-offset-4 hover:decoration-foreground ${FOCUS_RING}`;

interface SpecRow {
  label: string;
  value: string;
}

const SPEC_SHEET: SpecRow[] = [
  { label: 'Building since', value: '2021' },
  { label: 'Focus', value: 'Contractors and field services' },
  { label: 'Scope', value: 'Bid to books' },
  { label: 'Engagement', value: 'Direct with the builder' },
];

interface ProofCell {
  figure: string;
  caption: string;
}

const PROOF: ProofCell[] = [
  { figure: '2021', caption: 'Building operations software since' },
  { figure: '3 yrs', caption: 'Longest system in continuous daily use' },
  { figure: '1', caption: 'System per company, not a stack of subscriptions' },
  { figure: '0', caption: 'Packaged products the team has to contort around' },
];

interface CaseStudy {
  industry: string;
  title: string;
  timeline: string;
  shape: string;
  summary: string;
  capabilities: string[];
}

const CASES: CaseStudy[] = [
  {
    industry: 'Energy-infrastructure services contractor · employee-owned',
    title: 'The whole operation, one system',
    timeline: '2023 to present',
    shape: 'Company-wide operations system, replaced a patchwork of tools',
    summary:
      'A single system for a contractor running crews across several regions. A job is estimated, scheduled, worked, ticketed, invoiced, and costed in one place, and finance, safety, and fleet share that same database. Month-end reports are read from the system rather than assembled from spreadsheets.',
    capabilities: [
      'Estimating with bid comparison, and quotes the customer signs online',
      'Invoicing with approvals, disputes, billing packets, and card payment links',
      'Job cost and WIP by period, with unbilled and project rollup reports',
      'Purchase orders, vendor bills, account budgets, and credit-card matching',
      'Timecards, overtime rules, per diem, and certified payroll documents',
      'Crew scheduling, technician assignments, and vacation requests',
      'Fleet cost-per-mile, maintenance orders, pre-shift inspections, and GPS geofences',
      'Hot work, energy isolation, and excavation permits, JSAs, and incident reports',
      'Training records with expiration alerts',
      'Leads, client contacts, and field tickets that flow straight into billing',
      'Document cabinet for policies and shared files',
      'QuickBooks export batches and a token-secured external API',
      'Assistant with semantic search across records and receipt scanning',
    ],
  },
  {
    industry: 'Concrete and masonry subcontractor',
    title: 'Estimate to schedule in five weeks',
    timeline: 'Five weeks, 2026',
    shape: 'Focused operations app, mobile-first for the crew',
    summary:
      'A focused app for a subcontractor who bids to general contractors. The owner needed one screen showing which crews are committed where over the coming quarter, and whether awarded and tentative work fits the workforce. Built mobile-first so the field can read the schedule and sign change orders from a phone.',
    capabilities: [
      'Manpower board and company-wide Gantt with weekly capacity',
      'Projects with award state, phases, and hour budgets',
      'Estimating with a reusable catalog, cost buckets, alternates, and history with restore',
      'Quote PDFs on letterhead, frozen from the estimate with markup',
      'Change orders signed on a phone, typed or drawn, with time and IP recorded',
      'Workforce gap and utilization outlook from 2 to 52 weeks',
      'Monthly revenue forecast from contract amounts',
      'Submittals with review status, plus plans, photos, and contracts',
      'Jobsites with geocoded addresses',
      'Roles with per-person price visibility, and view-as for support',
    ],
  },
  {
    industry: 'Trade contractors · subscription product',
    title: 'Job cost and labor forecasting, as a product',
    timeline: 'Through 2023',
    shape: 'Multi-tenant product with accounts, teams, and billing',
    summary:
      'A subscription product for trade contractors who run on crews and cost codes. It answered the question owners kept asking: given the jobs we have and the ones we might win, do we have the people, and are we making money on each one. Budget against actual by cost code, gross profit by crew, and labor demand projected out a year.',
    capabilities: [
      'Jobs, change orders, and cost codes with budget against actual',
      'Labor hours forecast at 1, 3, 6, 9, and 12 months',
      'Crew workload, employee need, and utilization',
      'Gross profit by project, crew, and division',
      'Monthly actuals, variance, and company goals',
      'Calendar scheduling for crews and jobs',
      'Dashboards and charts for project managers',
      'Accounts, teams, roles, and subscription billing',
    ],
  },
  {
    industry: 'CAP Rock Labs · internal',
    title: 'The workspace this studio runs on',
    timeline: '2026 to present',
    shape: 'Issue and project tracker, one workspace per client company',
    summary:
      'The tool CAP Rock Labs runs on, built the same way client systems are. Issues, projects, cycles, and documents for each client company sit in one workspace, with a triage inbox that pulls in captured notes and pull requests so nothing waits in a chat thread. It is also where patterns are proven before they go into client work.',
    capabilities: [
      'Issues with team keys, sub-issues, relations, priorities, and cycles',
      'Query language for filtering by status, assignee, label, and priority',
      'Saved views, favorites, and list, board, and timeline displays',
      'Projects with milestones, todos, notes, and status updates',
      'Triage inbox merging urgent issues, captured notes, pull requests, and comments',
      'GitHub webhook that files pull requests against the right project',
      'Note-capture webhook with proposed routing a person accepts or rejects',
      'Full issue history and activity timeline',
      'Email notifications on assignment and comments',
      'Command palette and keyboard shortcuts throughout',
      'Per-company scoping, roles, and session management',
    ],
  },
];

interface Capability {
  title: string;
  body: string;
}

const CAPABILITIES: Capability[] = [
  {
    title: 'Estimating and quoting',
    body: 'Line-item estimates, catalogs, revisions, and quotes the customer can sign online.',
  },
  {
    title: 'Scheduling and workforce',
    body: 'Crew boards, Gantt views, utilization, and the gap between workload and workforce.',
  },
  {
    title: 'Job cost and finance',
    body: 'Job cost, WIP, invoicing, purchasing, payroll, and exports to the accounting system already in use.',
  },
  {
    title: 'Field and fleet',
    body: 'Field tickets, inspections, equipment, vehicles, maintenance, and GPS in the same system as the work.',
  },
  {
    title: 'Safety and compliance',
    body: 'Permits, JSAs, incident reports, training records, and the expirations that follow them.',
  },
  {
    title: 'Customer and client access',
    body: 'Client logins for selections, signatures, payments, and following the work without a phone call.',
  },
];

interface ApproachStep {
  number: string;
  title: string;
  body: string;
}

const APPROACH: ApproachStep[] = [
  {
    number: '01',
    title: 'Learn the operation',
    body: 'Ride along with how the work is bid, scheduled, done, and billed today, including the spreadsheets nobody admits to.',
  },
  {
    number: '02',
    title: 'Design the workflow',
    body: 'Turn that into a small number of clear workflows the team will actually follow, and agree on them before code.',
  },
  {
    number: '03',
    title: 'Build custom, ship weekly',
    body: 'One system, built for this company, delivered in small pieces the team starts using right away.',
  },
  {
    number: '04',
    title: 'Treat finance as operations',
    body: 'Job cost, WIP, and billing live next to the work, so the books reflect the field without a month-end scramble.',
  },
];

interface EyebrowProps {
  children: ReactNode;
}

// The bronze is the rule only. At this size bronze text on paper is too faint to read.
const Eyebrow = ({ children }: EyebrowProps) => (
  <div className="flex items-center gap-3">
    <span className="bg-accent h-px w-6 shrink-0" aria-hidden="true" />
    <span className="text-muted-foreground text-xs font-medium tracking-[0.18em] uppercase">
      {children}
    </span>
  </div>
);

interface Props {
  signupOpen: boolean;
}

const Show = ({ signupOpen }: Props) => {
  return (
    <div className="bg-background text-foreground">
      <section className={`${CONTAINER} py-14 sm:py-24`}>
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="flex flex-col gap-6 lg:col-span-7">
            <Eyebrow>Custom operations software</Eyebrow>
            <h1 className="authority-display text-4xl leading-[1.08] text-balance sm:text-6xl">
              Software built around how the company actually runs.
            </h1>
            <p className="text-muted-foreground max-w-xl text-lg leading-relaxed">
              CAP Rock Labs designs the workflows a contracting company operates on, then builds the
              system those operations need. Estimating, scheduling, job cost, field, fleet, and
              finance, in one place instead of six.
            </p>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3 text-sm">
              <a href={`mailto:${CONTACT_EMAIL}`} className={`font-medium ${TEXT_LINK}`}>
                Email us
              </a>
              <a
                href="#work"
                className={`text-muted-foreground hover:text-foreground ${FOCUS_RING}`}
              >
                See the work <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <Eyebrow>At a glance</Eyebrow>
            <dl className="mt-5 border-t">
              {SPEC_SHEET.map((row) => (
                <div
                  key={row.label}
                  className="grid grid-cols-[minmax(0,1fr)_auto] gap-6 border-b py-3 text-sm"
                >
                  <dt className="text-muted-foreground">{row.label}</dt>
                  <dd className="text-right font-medium">{row.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      <section aria-label="In numbers" className="bg-card border-t">
        <dl className="mx-auto grid max-w-6xl lg:grid-cols-4">
          {PROOF.map((cell) => (
            <div
              key={cell.caption}
              className="border-t px-4 py-6 first:border-t-0 sm:px-6 lg:border-t-0 lg:border-l lg:px-8 lg:first:border-l-0"
            >
              <dt className="text-2xl leading-none font-medium tabular-nums">{cell.figure}</dt>
              <dd className="text-muted-foreground mt-2 text-sm leading-relaxed">{cell.caption}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section id="work" className={SECTION}>
        <div className={CONTAINER}>
          <div className="flex max-w-2xl flex-col gap-4">
            <Eyebrow>Selected work</Eyebrow>
            <h2 className="text-3xl tracking-tight sm:text-4xl">
              Four systems, each built around one operation.
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Client names are kept private. The scope is not.
            </p>
          </div>

          <div className="mt-10">
            {CASES.map((study) => (
              <article
                key={study.title}
                className="grid gap-8 border-t py-10 lg:grid-cols-12 lg:gap-12"
              >
                <div className="flex flex-col gap-4 lg:col-span-4">
                  <p className="text-muted-foreground text-sm leading-relaxed">{study.industry}</p>
                  <h3 className="text-2xl leading-tight tracking-tight">{study.title}</h3>
                  <dl className="border-t text-sm">
                    <div className="flex flex-col gap-1 border-b py-3">
                      <dt className="text-muted-foreground">Timeline</dt>
                      <dd className="font-medium">{study.timeline}</dd>
                    </div>
                    <div className="flex flex-col gap-1 border-b py-3">
                      <dt className="text-muted-foreground">Shape</dt>
                      <dd className="font-medium">{study.shape}</dd>
                    </div>
                  </dl>
                </div>

                <div className="flex flex-col gap-5 lg:col-span-8">
                  <p className="text-muted-foreground leading-relaxed">{study.summary}</p>
                  <Eyebrow>What was built</Eyebrow>
                  <ul className="border-t">
                    {study.capabilities.map((capability) => (
                      <li key={capability} className="border-b py-2 text-sm leading-snug">
                        {capability}
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={SECTION}>
        <div className={CONTAINER}>
          <div className="flex max-w-2xl flex-col gap-4">
            <Eyebrow>What we build</Eyebrow>
            <h2 className="text-3xl tracking-tight sm:text-4xl">
              Operations software, end to end.
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Every module below has shipped at least once. Pick the ones the company needs; they
              share one database and one login.
            </p>
          </div>

          <dl className="mt-10 border-t">
            {CAPABILITIES.map((capability) => (
              <div
                key={capability.title}
                className="grid gap-2 border-b py-5 sm:grid-cols-[16rem_1fr] sm:items-baseline sm:gap-10"
              >
                <dt className="font-medium">{capability.title}</dt>
                <dd className="text-muted-foreground text-sm leading-relaxed">{capability.body}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className={SECTION}>
        <div className={CONTAINER}>
          <div className="flex max-w-2xl flex-col gap-4">
            <Eyebrow>How we work</Eyebrow>
            <h2 className="text-3xl tracking-tight sm:text-4xl">
              Sit with the business first. Then build.
            </h2>
          </div>

          <ol className="mt-10 border-t">
            {APPROACH.map((step) => (
              <li
                key={step.number}
                className="grid gap-2 border-b py-5 sm:grid-cols-[3.5rem_1fr] sm:gap-6"
              >
                <span className="text-sm font-medium tabular-nums" aria-hidden="true">
                  {step.number}
                </span>
                <div className="flex max-w-2xl flex-col gap-1">
                  <h3 className="font-medium">{step.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className={SECTION}>
        <div className={`${CONTAINER} grid gap-8 lg:grid-cols-2 lg:items-end lg:gap-16`}>
          <div className="flex flex-col gap-4">
            <Eyebrow>Contact</Eyebrow>
            <h2 className="max-w-lg text-3xl leading-tight tracking-tight sm:text-4xl">
              If this sounds like the work you need, email us.
            </h2>
          </div>

          <div className="flex flex-col gap-3 border-t pt-6 lg:border-t-0 lg:border-l lg:pt-0 lg:pl-10">
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className={`w-fit text-lg font-medium break-all sm:text-xl ${TEXT_LINK}`}
            >
              {CONTACT_EMAIL}
            </a>
            <p className="text-muted-foreground text-sm">Replies within one business day.</p>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Need something smaller than a full system? The platform builds an app from a
              description.{' '}
              <Link href={signup_path()} className={`text-foreground ${TEXT_LINK}`}>
                {signupOpen ? 'Get started' : 'Request access'}
              </Link>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Show;
